import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, AlertTriangle, CheckCircle, Smartphone, User, Lock, Eye } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { useSfx } from '../../../store/sfxStore';

interface Message {
    id: number;
    sender: string;
    subject?: string;
    body: string;
    type: 'email' | 'text' | 'voice';
    isFake: boolean;
    clues: string[];
}

export const AiPhishing = () => {
    const { completeModule } = useAcademyStore();
    const { play } = useSfx();
    const [completed, setCompleted] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [currentMsgIndex, setCurrentMsgIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState<{ correct: boolean; message: string } | null>(null);

    const messages: Message[] = [
        {
            id: 1,
            sender: "IT Support <security-alert@g0rilla-mart.com>", // Typo in domain (0 instead of o)
            subject: "URGENT: Password Expiry",
            body: "Your Gorilla Mart password expires in 2 hours. Click here to reset it immediatly.",
            type: 'email',
            isFake: true,
            clues: ["'g0rilla' domain typo", "Urgency ('in 2 hours')", "Spelling ('immediatly')"]
        },
        {
            id: 2,
            sender: "CEO <boss@gorilla-mart.com>",
            subject: "Conference Call",
            body: "Hey, I need you to buy 50 gift cards for the team event. Don't tell anyone, it's a surprise. Can you send codes now?",
            type: 'email',
            isFake: true,
            clues: ["Unusual financial request", "Secrecy ('Don't tell anyone')", "CEO asking junior staff directly"]
        },
        {
            id: 3,
            sender: "+1 (555) 019-2834",
            body: "GorillaMart Security Code: 839201. Don't share this.",
            type: 'text',
            isFake: false,
            clues: ["Short, informational", "No link to click", "Standard 2FA format"]
        },
        // AI Voice Clone scenario simulated via text description
        {
            id: 4,
            sender: "Voicemail: 'Mom'",
            body: "(Audio Transcript): 'Hey honey! It's Mom. I'm... stuck in jail in Mexico? Please wire $5,000 to this account. Don't call dad. Love you.'",
            type: 'voice',
            isFake: true,
            clues: ["Voice Clone (High emotion/Urgency)", "Unverified location", "Financial demand", "Secrecy"]
        }
    ];

    const handleDecision = (choice: 'fake' | 'safe') => {
        const msg = messages[currentMsgIndex];
        const isCorrect = (choice === 'fake' && msg.isFake) || (choice === 'safe' && !msg.isFake);

        if (isCorrect) {
            play('success');
            setScore(s => s + 1);
            setFeedback({ correct: true, message: "Correct! Nice spotting." });
        } else {
            play('error');
            setFeedback({
                correct: false,
                message: msg.isFake
                    ? `Missed it! Clues: ${msg.clues.join(", ")}`
                    : "That was actually safe. Better paranoid than hacked though!"
            });
        }

        setTimeout(() => {
            setFeedback(null);
            if (currentMsgIndex < messages.length - 1) {
                setCurrentMsgIndex(i => i + 1);
            } else {
                // End game
                if (score >= 2) { // Allow 1-2 mistakes, it's learning
                    if (!completed) {
                        play('glory');
                        setCompleted(true);
                        completeModule('ai-phishing', 100);
                        setShowSuccess(true);
                    }
                } else {
                    // Retry
                    play('error');
                    alert("The hackers got you! Try again and look closer at the clues.");
                    setCurrentMsgIndex(0);
                    setScore(0);
                }
            }
        }, 2500);
    };

    const currentMsg = messages[currentMsgIndex];

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-slate-100 pt-24">
            <div className="max-w-4xl mx-auto p-8">
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-black font-comic text-red-500 mb-4 flex items-center justify-center gap-3">
                        <Eye size={40} /> Deepfake Detective
                    </h1>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                        AI can write perfect emails and clone voices. Can you spot the bots?
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">

                    {/* Device Simulator */}
                    <div className="bg-slate-800 rounded-[3rem] border-8 border-slate-700 p-4 shadow-2xl relative min-h-[500px] flex flex-col">
                        {/* Notch */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-700 rounded-b-xl z-20"></div>

                        {/* Screen Content */}
                        <div className="flex-1 bg-white rounded-2xl overflow-hidden text-slate-900 flex flex-col">
                            {/* App Header */}
                            <div className="bg-slate-100 p-4 border-b flex items-center gap-2 pt-8">
                                <div className={`p-2 rounded-full ${currentMsg.type === 'email' ? 'bg-blue-100 text-blue-600' :
                                    currentMsg.type === 'text' ? 'bg-green-100 text-green-600' :
                                        'bg-purple-100 text-purple-600'
                                    }`}>
                                    {currentMsg.type === 'email' ? <Lock size={16} /> : currentMsg.type === 'text' ? <Smartphone size={16} /> : <User size={16} />}
                                </div>
                                <div className="text-sm font-bold truncate flex-1">{currentMsg.sender}</div>
                            </div>

                            {/* Message Body */}
                            <div className="p-6 flex-1 flex flex-col justify-center">
                                {currentMsg.subject && (
                                    <div className="font-bold text-lg mb-2">{currentMsg.subject}</div>
                                )}
                                <div className="bg-slate-100 p-4 rounded-xl rounded-tl-none">
                                    {currentMsg.body}
                                </div>
                                {currentMsg.type === 'voice' && (
                                    <div className="mt-2 text-xs text-slate-400 italic text-center">
                                        Audio file playing... 🔈
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="p-4 grid grid-cols-2 gap-4 pb-8">
                                <button
                                    onClick={() => { handleDecision('fake'); play('click'); }}
                                    onMouseEnter={() => play('hover')}
                                    className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
                                >
                                    <AlertTriangle size={18} /> Fake!
                                </button>
                                <button
                                    onClick={() => { handleDecision('safe'); play('click'); }}
                                    onMouseEnter={() => play('hover')}
                                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
                                >
                                    <CheckCircle size={18} /> It's Safe
                                </button>
                            </div>

                            {/* Feedback Overlay */}
                            <AnimatePresence>
                                {feedback && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 50 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        className={`absolute inset-0 z-30 flex items-center justify-center p-6 text-center backdrop-blur-sm ${feedback.correct ? 'bg-green-500/90' : 'bg-red-500/90'
                                            }`}
                                    >
                                        <div>
                                            <div className="text-6xl mb-4">{feedback.correct ? '✅' : '❌'}</div>
                                            <h3 className="text-2xl font-black text-white mb-2">
                                                {feedback.correct ? 'Caught!' : 'Ouch!'}
                                            </h3>
                                            <p className="text-white font-medium">{feedback.message}</p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Score / Info */}
                    <div className="space-y-6">
                        <div className="bg-slate-800 p-6 rounded-3xl border border-slate-700">
                            <h3 className="text-slate-400 uppercase text-xs font-bold tracking-wider mb-2">Current Score</h3>
                            <div className="text-4xl font-black text-white">{score} / {messages.length}</div>
                        </div>

                        <div className="bg-slate-800 p-6 rounded-3xl border border-slate-700">
                            <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                                <AlertTriangle size={20} /> AI Red Flags
                            </h3>
                            <ul className="space-y-3 text-sm text-slate-300">
                                <li className="flex gap-2">
                                    <span className="text-red-500 font-bold">•</span>
                                    Urgency & Fear ("Do this NOW")
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-red-500 font-bold">•</span>
                                    Weird Typos (AI is getting better, but g0rilla vs gorilla)
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-red-500 font-bold">•</span>
                                    Unverified Secrecy ("Don't tell anyone")
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-red-500 font-bold">•</span>
                                    <strong>Audio:</strong> Background noise cuts out, robotic inflection
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

            </div>

            {showSuccess && (
                <ModuleSuccess
                    title="Threat Detected!"
                    onRetry={() => setShowSuccess(false)}
                >
                    <div className="space-y-4">
                        <p className="text-slate-600">
                            You successfully identified the AI-enhanced attacks.
                        </p>
                        <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                            <h4 className="font-bold text-red-800 text-sm mb-2">Defense Strategy:</h4>
                            <p className="text-red-700 text-sm">
                                "Trust but Verify." If a CEO asks for gift cards, call them. If a voice sounds like Mom but asks for wire transfers, ask a personal question AI wouldn't know!
                            </p>
                        </div>
                    </div>
                </ModuleSuccess>
            )}
        </div>
    );
};
